import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { fetchUserPackages, packageCheckoutAbandon, packageCheckoutInit, packagePaymentFormSubmit } from '../../../actions/creators/packages';
import { getPackages, isPackagesLoading, getPackagesError, getPackagePurchaseError, getPackagePurchaseSuccess, isPackagePurchaseProcessing } from '../../../reducers';
import LogoLoading from '../../LogoLoading';
import { breakpoints } from '../../../utils/styles';
import Button from '../../Button';
import PaymentSidebar from '../../checkout/PaymentSidebar';
import { CHECKOUT_CONTEXT_LOYALTY_PACKAGES } from '../../../actions/creators/checkout';
import LoyaltyPurchaseOption from './LoyaltyPurchaseOption';
import PurchaseSuccessMessage from './PurchaseSuccessMessage';
import CardAnimation from '../../checkout/CardAnimation';

const PackagesGrid = styled.div`
  margin-top: 2rem;

  @media screen and (min-width: ${breakpoints.desktop}px) {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 2rem;
  }
`;

const ErrorContainer = styled.div`
  background: #fdf2f2;
  border-radius: 8px;
  color: #e74c3c;
  margin: 2rem 0;
  padding: 3rem;
  text-align: center;
`;

const EmptyContainer = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  color: #666;
  margin: 2rem 0;
  padding: 3rem;
  text-align: center;
`;

interface Props {
  error: string | null;
  isLoading: boolean;
  isPurchasing: boolean;
  packages: any[];
  paymentGateway: string;
  purchaseError: string | null;
  successData: any;
  fetchUserPackages: () => void;
  onLoad: () => void;
  packageCheckoutAbandon: () => void;
  packageCheckoutInit: (data: any) => void;
  packagePaymentFormSubmit: (data: any) => void;
}

interface State {
  selectedItems: any[] | null;
}

class PackagesPage extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.handleBackToPackages = this.handleBackToPackages.bind(this);

    this.state = {
      selectedItems: null
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.props.onLoad();
    this.props.fetchUserPackages();
  }

  handlePurchasePackage = (packageData) => {
    const items = [{
      id: packageData.id,
      name: packageData.name,
      description: packageData.description,
      quantity: 1,
      price: packageData.price
    }];

    this.setState({
      selectedItems: items
    })
  };

  handleBackToPackages = () => {
    this.setState({
      selectedItems: null
    });
  };

  handlePaymentSubmit = (paymentData) => {
    this.props.packagePaymentFormSubmit(paymentData);
  };

  handlePaymentSuccess = () => {
    this.handleBackToPackages();
  };

  handleRetryPayment = () => {
    this.props.packageCheckoutAbandon();
  };

  render() {
    const { packages, isLoading, error } = this.props;

    if (isLoading) {
      return <LogoLoading />;
    }

    if (this.props.successData) {
      return this.renderSuccessScreen();
    }

    return (
      <div>
        <p>
          Elige la opción de paquete más te guste y disfruta los beneficios de ser Cinemex Loop.
        </p>

        {error && (
          <ErrorContainer>
            <h3>Error al cargar los paquetes</h3>
            <p>Por favor, intenta nuevamente más tarde.</p>
          </ErrorContainer>
        )}

        {!packages.length && !error && (
          <EmptyContainer>
            <h3>No tienes paquetes disponibles</h3>
            <p>En este momento no hay paquetes disponibles para tu cuenta.</p>
          </EmptyContainer>
        )}
        
        <PackagesGrid>
          {packages.map(pkg => <LoyaltyPurchaseOption
              key={pkg.id}
              option={pkg}
              onSelect={this.handlePurchasePackage}
              selected={this.state.selectedItems && this.state.selectedItems.length ? this.state.selectedItems[0].id === pkg.id : false}
            />
          )}
        </PackagesGrid>

        {this.renderPaymentSidebar()}
        {this.props.isPurchasing && <CardAnimation/>}
      </div>
    );
  }

  renderPaymentSidebar() {
    if (!this.state.selectedItems || !this.state.selectedItems.length) {
      return null;
    }

    return (
      <PaymentSidebar
        context={CHECKOUT_CONTEXT_LOYALTY_PACKAGES}
        cash={this.state.selectedItems[0].price}
        ccRequired
        paymentGateway={this.props.paymentGateway}
        method="credit-card"
        transactionData={this.getTransactionData()}
        onCancel={this.handleBackToPackages}
      />
    );
  }

  renderSuccessScreen() {
    let subtitle = this.state.selectedItems && this.state.selectedItems.length ? `Has comprado el paquete ${this.state.selectedItems[0].name.toUpperCase()} 🙌` : undefined;

    return <PurchaseSuccessMessage
      title="¡FELICIDADES!"
      subtitle={subtitle}
      content={
        <>
          <p>Los beneficios ya han sido asignados en tu tarjeta Cinemex Loop.</p>
          <p>Puedes utilizarlos en taquilla, dulcería y nuestros medios digitales. ¡Que los disfrutes!</p>
        </>
      }
      buttons={
        <Button to="/usuario/loop/tarjeta">Ver tu tarjeta</Button>
      }
    />;
  }

  getTransactionData() {
    const data = {
      items: this.state.selectedItems
    };

    return {
      context: CHECKOUT_CONTEXT_LOYALTY_PACKAGES,
      data
    };
  }
}

const mapStateToProps = state => ({
  packages: getPackages(state),
  isLoading: isPackagesLoading(state),
  isPurchasing: isPackagePurchaseProcessing(state),
  error: getPackagesError(state),
  paymentGateway: 'stripe-elements', // @todo make this dynamic
  purchaseError: getPackagePurchaseError(state),
  successData: getPackagePurchaseSuccess(state),
});

const mapDispatchToProps = dispatch => ({
  onLoad: () => dispatch(packageCheckoutInit()),
  fetchUserPackages: () => dispatch(fetchUserPackages()),
  packageCheckoutAbandon: () => dispatch(packageCheckoutAbandon()),
  packageCheckoutInit: (data) => dispatch(packageCheckoutInit(data)),
  packagePaymentFormSubmit: (data) => dispatch(packagePaymentFormSubmit(data)),
});

const PackagesPageConnected = connect(mapStateToProps, mapDispatchToProps)(PackagesPage);

export default PackagesPageConnected;